import brandNamesConstants from '@constants/brandNames.constants'
import {getEnvVariable, getLocalAppOrigin, sanitizeBaseUrl} from './envUtils'

type BrandUrlType = 'portalUrl' | 'termsAndConditions' | 'privacyPolicy'
type BrandUrlEnvKeys = Record<BrandUrlType, string>

const brandUrlEnvKeysByBrand: Record<string, BrandUrlEnvKeys> = {
  [brandNamesConstants.DENTALSTACK]: {
    portalUrl: 'REACT_APP_PORTAL_URL_DENTALSTACK',
    termsAndConditions: 'REACT_APP_TERMS_AND_CONDITIONS_URL_DENTALSTACK',
    privacyPolicy: 'REACT_APP_PRIVACY_POLICY_URL_DENTALSTACK',
  },
  [brandNamesConstants.SMILEZY]: {
    portalUrl: 'REACT_APP_PORTAL_URL_SMILEZY',
    termsAndConditions: 'REACT_APP_TERMS_AND_CONDITIONS_URL_SMILEZY',
    privacyPolicy: 'REACT_APP_PRIVACY_POLICY_URL_SMILEZY',
  },
  [brandNamesConstants.ROUTETOSMILE]: {
    portalUrl: 'REACT_APP_PORTAL_URL_ROUTETOSMILE',
    termsAndConditions: 'REACT_APP_TERMS_AND_CONDITIONS_URL_ROUTETOSMILE',
    privacyPolicy: 'REACT_APP_PRIVACY_POLICY_URL_ROUTETOSMILE',
  },
  [brandNamesConstants.CRAFTALIGN]: {
    portalUrl: 'REACT_APP_PORTAL_URL_CRAFTALIGN',
    termsAndConditions: 'REACT_APP_TERMS_AND_CONDITIONS_URL_CRAFTALIGN',
    privacyPolicy: 'REACT_APP_PRIVACY_POLICY_URL_CRAFTALIGN',
  },
  [brandNamesConstants.SYNAPSE]: {
    portalUrl: 'REACT_APP_PORTAL_URL_SYNAPSE',
    termsAndConditions: 'REACT_APP_TERMS_AND_CONDITIONS_URL_SYNAPSE',
    privacyPolicy: 'REACT_APP_PRIVACY_POLICY_URL_SYNAPSE',
  },
  [brandNamesConstants.CLEARCASTLE]: {
    portalUrl: 'REACT_APP_PORTAL_URL_CLEARCASTLE',
    termsAndConditions: 'REACT_APP_TERMS_AND_CONDITIONS_URL_CLEARCASTLE',
    privacyPolicy: 'REACT_APP_PRIVACY_POLICY_URL_CLEARCASTLE',
  },
  [brandNamesConstants.SMILEXCEL]: {
    portalUrl: 'REACT_APP_PORTAL_URL_SMILEXCEL',
    termsAndConditions: 'REACT_APP_TERMS_AND_CONDITIONS_URL_SMILEXCEL',
    privacyPolicy: 'REACT_APP_PRIVACY_POLICY_URL_SMILEXCEL',
  },
  [brandNamesConstants.AIIQALIGNER]: {
    portalUrl: 'REACT_APP_PORTAL_URL_AIIQALIGNER',
    termsAndConditions: 'REACT_APP_TERMS_AND_CONDITIONS_URL_AIIQALIGNER',
    privacyPolicy: 'REACT_APP_PRIVACY_POLICY_URL_AIIQALIGNER',
  },
  [brandNamesConstants.CONFIDENTALIGNER]: {
    portalUrl: 'REACT_APP_PORTAL_URL_CONFIDENTALIGNER',
    termsAndConditions: 'REACT_APP_TERMS_AND_CONDITIONS_URL_CONFIDENTALIGNER',
    privacyPolicy: 'REACT_APP_PRIVACY_POLICY_URL_CONFIDENTALIGNER',
  },
  [brandNamesConstants.ROUTETOSMILEVSP]: {
    portalUrl: 'REACT_APP_PORTAL_URL_ROUTETOSMILEVSP',
    termsAndConditions: 'REACT_APP_TERMS_AND_CONDITIONS_URL_ROUTETOSMILEVSP',
    privacyPolicy: 'REACT_APP_PRIVACY_POLICY_URL_ROUTETOSMILEVSP',
  },
}

type BrandPortalUrlOptions = {
  includeTrailingSlash?: boolean
  preferLocalOrigin?: boolean
}

export const normalizeBrandName = (brand: string | null | undefined): string =>
  brand?.toUpperCase().replace(/\s+/g, '').trim() || brandNamesConstants.DENTALSTACK

const includeTrailingSlash = (url: string): string => {
  if (!url) return ''

  return `${sanitizeBaseUrl(url)}/`
}

const getBrandUrlEnvKey = (brand: string, urlType: BrandUrlType): string =>
  brandUrlEnvKeysByBrand[brand]?.[urlType] ||
  brandUrlEnvKeysByBrand[brandNamesConstants.DENTALSTACK][urlType]

const getConfiguredBrandUrl = (brand: string, urlType: BrandUrlType): string => {
  return getEnvVariable(getBrandUrlEnvKey(brand, urlType))
}

const getConfiguredPortalUrl = (brand: string): string => {
  const envKey = getBrandUrlEnvKey(brand, 'portalUrl')
  const portalUrl = sanitizeBaseUrl(getEnvVariable(envKey))
  const isDentalStackPortal =
    envKey === brandUrlEnvKeysByBrand[brandNamesConstants.DENTALSTACK].portalUrl

  if (portalUrl || !isDentalStackPortal) return portalUrl

  return sanitizeBaseUrl(getEnvVariable('REACT_APP_BASE_APP_URL'))
}

export const getBrandPortalUrl = (
  brand: string | null | undefined,
  options: BrandPortalUrlOptions = {}
): string => {
  const normalizedBrand = normalizeBrandName(brand)
  const localAppOrigin = options.preferLocalOrigin ? getLocalAppOrigin() : ''
  const portalUrl = localAppOrigin || getConfiguredPortalUrl(normalizedBrand)

  return options.includeTrailingSlash ? includeTrailingSlash(portalUrl) : portalUrl
}

export const getBrandTermsAndConditionsUrl = (brand: string | null | undefined): string => {
  return getConfiguredBrandUrl(normalizeBrandName(brand), 'termsAndConditions')
}

export const getBrandPrivacyPolicyUrl = (brand: string | null | undefined): string => {
  return getConfiguredBrandUrl(normalizeBrandName(brand), 'privacyPolicy')
}

export const getContactUsUrl = (): string => getEnvVariable('REACT_APP_CONTACT_US_URL')
